import { apiService } from './services/apiService';
import { evaluateMarkerReference, type MarkerEvaluationContext } from './lib/referenceIntervalEngine';

// Single source of truth to avoid "Cannot access X before initialization" when
// DEFAULT_SERVER_URL is imported before SERVER in bundled output.
const isLocal = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
const isViteDev = import.meta.env.DEV;
const configuredServerUrl = import.meta.env.VITE_SERVER_URL;
const requiresConfiguredServerUrl = import.meta.env.PROD;
const defaultLocalServerUrl = isViteDev ? 'http://localhost:3001' : '';
if (requiresConfiguredServerUrl && !configuredServerUrl) {
  throw new Error('Missing VITE_SERVER_URL for production/mobile release build.');
}
const BASE_SERVER_URL = isLocal
  ? (isViteDev ? '' : (configuredServerUrl || defaultLocalServerUrl))
  : (configuredServerUrl || (requiresConfiguredServerUrl ? '' : defaultLocalServerUrl));
export const SERVER = BASE_SERVER_URL;

export interface Msg {
  role: 'user' | 'assistant';
  content: string | null;
  timestamp?: string;
  imageUrl?: string;
  filename?: string;
  metadata?: Record<string, any>;
}

export interface Conversation {
  id: string;
  messages: Msg[];
  title?: string;
  timestamp?: string;  // Legacy
  updatedAt?: string;  // New standard
  createdAt?: string;
  messageCount?: number;
}

export interface QueuedAttachment {
  id?: string;
  file: File;
  label: string;
  kind: 'image' | 'pdf' | 'audio' | 'other';
  sizeLabel: string;
  previewUrl?: string; // For images
}

export type PrimaryView =
  | 'today'
  | 'chat'
  | 'health'
  | 'you'
  | 'insights'
  | 'progress'
  | 'calendar'
  | 'cycle'
  | 'family'
  | 'more'
  | 'plan'
  | 'bodyModel3D'
  // Legacy views maintained for backward compatibility
  | 'vitals'
  | 'protocols'
  | 'labs'
  | 'community'
  | 'achievements'
  | 'scan';

export type MobileRootTab =
  | 'today'
  | 'chat'
  | 'health'
  | 'scan'
  | 'protocols'
  | 'you';

export const MOBILE_ROOT_TABS: ReadonlyArray<{
  id: MobileRootTab;
  label: string;
  view: PrimaryView;
  description: string;
}> = [
  {
    id: 'today',
    label: 'Today',
    view: 'today',
    description: 'Daily brief, readiness, and next actions'
  },
  {
    id: 'chat',
    label: 'Chat',
    view: 'chat',
    description: 'Talk with NOOX and review recent conversations'
  },
  {
    id: 'health',
    label: 'Health',
    view: 'health',
    description: 'Labs, vitals, records, and data sources'
  },
  {
    id: 'scan',
    label: 'Scan',
    view: 'scan',
    description: 'Capture labs, labels, and uploads'
  },
  {
    id: 'protocols',
    label: 'Protocols',
    view: 'protocols',
    description: 'Daily plan, check-ins, and recommendations'
  },
  {
    id: 'you',
    label: 'You',
    view: 'you',
    description: 'Account, subscription, settings, and trusted actions'
  }
] as const;

export interface MemoryEditTarget {
  id?: string;
  content?: string;
  type: string;
  marker?: string;
  entry?: any;
}

export interface MemoryEditForm {
  content: string;
  type: string;
  date: string;
  tags: string[];
  value: string;
  note: string;
  summary: string;
}

export const EMPTY_MEMORY_FORM: MemoryEditForm = {
  content: '',
  tags: [],
  type: 'note',
  value: '',
  date: '',
  note: '',
  summary: ''
};

export type ProtocolFilter = 'active' | 'paused' | 'completed' | 'all';
export type ProtocolCategory = 'supplement' | 'medication' | 'diet' | 'exercise' | 'mindfulness' | 'other' | 'holistic';

export interface ProtocolBlueprint {
  id: string;
  title: string;
  description: string;
  category: ProtocolCategory;
  evidenceStrength: 'strong' | 'emerging' | 'hypothetical';
  costEstimate: string;
  timeCommitment: string;
  tags: string[];
  defaultFrequency?: string;
  sourceUrl?: string;
  author?: string;
  verificationStatus?: 'verified' | 'community' | 'flagged';
  imageUrl?: string;
}

export interface Protocol {
  id: string;
  title?: string;
  description?: string;
  category: ProtocolCategory;
  status: 'active' | 'paused' | 'completed';
  startedAt?: string;
  notes?: string;
  progress?: any[];
  customFrequency?: string;
  blueprint?: ProtocolBlueprint;
  actions?: any[];
  focus?: string;
  target?: string;
  cadence?: string;
  baselineValue?: number;
  targetValue?: number;
  metric?: string;
  metricUnit?: string;
  unit?: string;
  evidenceStrength?: 'strong' | 'emerging' | 'hypothetical';
  costEstimate?: string;
  timeCommitment?: string;
  tags?: string[];
  defaultFrequency?: string;
  sourceUrl?: string;
  author?: string;
  verificationStatus?: 'verified' | 'community' | 'flagged';
  imageUrl?: string;
}

export const createIntroMessage = (missionCompletion?: number | null, nextMission?: { id: string; title: string } | null, allowMemory = true): Msg => {
  // If activation is incomplete, show onboarding message
  if (missionCompletion !== undefined && missionCompletion !== null && missionCompletion < 100 && nextMission) {
    const nextStep = `Let's start by ${nextMission.title.toLowerCase()}. `;
    return {
      role: 'assistant',
      content: `Hi — I’m NOOX, your intelligent health reasoning system.\n\n${nextStep}What would you like to do first?`,
      timestamp: new Date().toISOString()
    };
  }
  // Default message for completed activation or when info not available
  return {
    role: 'assistant',
    content: allowMemory
      ? 'Hi — I’m NOOX, your intelligent health reasoning system.\n\nI remember your health history across conversations, so you can ask about your labs, protocols, and goals.\n\nWhat would you like to focus on today?'
      : 'Hi — I’m NOOX, your intelligent health reasoning system.\n\nYou’re in Snapshot mode, so I won’t remember this session after you leave.\n\nWhat would you like to analyze today?',
    timestamp: new Date().toISOString()
  };
};

export const DEFAULT_PLAN_CATALOG = [
  {
    id: 'explorer',
    label: 'Explorer',
    priceLabel: 'Free',
    description: 'Full daily scores + history with manual logging. No AI memory or coaching.',
    capUSD: 0,
    highlights: [
      'Daily Recovery, Strain, Sleep, Stress scores',
      'Unlimited history + trend views',
      'Strength builder + workout logging',
      'Nutrition logging + macro tracking',
      'Smart sleep alarm',
      'Habit journal'
    ]
  },
  {
    id: 'core',
    label: 'Core',
    priceLabel: '$12',
    description: 'Intelligent reasoning system that remembers you and evolves over time.',
    capUSD: 12,
    highlights: [
      'Persistent health memory',
      'Unlimited lab & document uploads',
      'Biomarker trend analysis',
      'Personalized protocol generation',
      'Evidence-strength explanations',
      'Workspace sharing',
      'Priority support'
    ]
  },
  {
    id: 'pro',
    label: 'Pro',
    priceLabel: '$25',
    description: 'Context-aware automation that runs alongside your life.',
    capUSD: 25,
    highlights: [
      'Calendar + wearable context',
      'Adaptive protocol adjustments',
      'Intelligent reminders',
      'Clinician-ready summaries',
      'Team workspaces + roles',
      'Custom onboarding'
    ]
  }
] as const;

export class ActionableError extends Error {
  action?: () => void;
  actionLabel?: string;
  constructor(message: string, action?: () => void, actionLabel?: string) {
    super(message);
    this.action = action;
    this.actionLabel = actionLabel;
  }
}

export const MAX_ATTACHMENTS = 5;
export const DEFAULT_ATTACHMENT_ACCEPT = "image/jpeg,image/png,image/webp,application/pdf,audio/mpeg,audio/wav,audio/webm,text/plain";
export const DEFAULT_SERVER_URL = BASE_SERVER_URL;

export interface GlobalSearchItem {
  id: string;
  type: 'protocol' | 'lab' | 'conversation' | 'other';
  title: string;
  subtitle?: string;
  action: () => void;
}

export type MarkerCategory = 'metabolic' | 'cardiometabolic' | 'hormonal' | 'inflammatory' | 'organ_function' | 'micronutrient' | 'other';
export type MarkerStatus = 'optimal' | 'watch' | 'attention' | 'unknown' | 'fair' | 'poor';
export type Role = 'owner' | 'editor' | 'viewer';

export const PRIMARY_VIEWS: PrimaryView[] = [
  'chat',
  'insights',
  'health',
  'protocols',
  'calendar',
  'cycle',
  'family'
];

export const formatPlanBadge = (label?: string | null) => {
  if (!label) return undefined;
  return label === 'Explorer' ? 'Explore' : label;
};

export const PRICING_FAQS = [
  { question: "Can I cancel anytime?", answer: "Yes, you can cancel your subscription at any time." },
  { question: "What is the Explorer plan?", answer: "The Explorer plan is free and allows you to track vitals manually." }
];

export const SUPPORT_FAQS = [
  { question: "How do I upload labs?", answer: "Go to Vitals or Chat and click the Upload button." }
];

export const PROTOCOL_CATEGORIES: ProtocolCategory[] = ['supplement', 'medication', 'diet', 'exercise', 'mindfulness', 'other', 'holistic'];
export const PROTOCOL_FILTERS: ProtocolFilter[] = ['active', 'paused', 'completed', 'all'];

export const domainOrder = [
  { id: 'cardiometabolic', title: 'Cardiometabolic', subtitle: 'Heart & Vascular health' },
  { id: 'metabolic', title: 'Metabolic', subtitle: 'Energy & Glucose' },
  { id: 'inflammatory', title: 'Inflammation', subtitle: 'Systemic status' },
  { id: 'hormonal', title: 'Hormonal', subtitle: 'Endocrine balance' },
  { id: 'organ_function', title: 'Organ Function', subtitle: 'Liver, Kidney, Thyroid' },
  { id: 'micronutrient', title: 'Micronutrients', subtitle: 'Vitamins & Minerals' },
  { id: 'other', title: 'Other Signals', subtitle: 'Wearables, fitness, and performance' }
];

export const biomarkersConfig: Record<string, { label: string; units: string; category: MarkerCategory; description?: string; insight?: string; optimalRange?: string }> = {
  apob: { label: 'ApoB', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '< 90' },
  apoa1: { label: 'ApoA1', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '> 120' },
  apob_apoa1_ratio: { label: 'ApoB/ApoA1 Ratio', units: '', category: 'cardiometabolic', optimalRange: '< 0.8' },
  ldl: { label: 'LDL-C', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '< 100' },
  ldl_c: { label: 'LDL-C', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '< 100' },
  ldl_p: { label: 'LDL-P', units: 'nmol/L', category: 'cardiometabolic', optimalRange: '< 1000' },
  small_ldl_p: { label: 'Small LDL-P', units: 'nmol/L', category: 'cardiometabolic', optimalRange: '< 500' },
  ldl_size: { label: 'LDL Size', units: 'nm', category: 'cardiometabolic', optimalRange: '> 20.5' },
  non_hdl: { label: 'Non-HDL-C', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '< 130' },
  hdl: { label: 'HDL-C', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '> 50' },
  hdl_c: { label: 'HDL-C', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '> 50' },
  total_cholesterol: { label: 'Total Cholesterol', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '< 200' },
  trig: { label: 'Triglycerides', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '< 150' },
  triglycerides: { label: 'Triglycerides', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '< 150' },
  tg: { label: 'Triglycerides', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '< 150' },
  tg_hdl_ratio: { label: 'TG/HDL Ratio', units: '', category: 'cardiometabolic', optimalRange: '< 2.0' },
  lpa: { label: 'Lipoprotein(a)', units: 'nmol/L', category: 'cardiometabolic', optimalRange: '< 75' },
  vldl: { label: 'VLDL-C', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '< 30' },
  remnant_cholesterol: { label: 'Remnant Cholesterol', units: 'mg/dL', category: 'cardiometabolic', optimalRange: '< 20' },
  oxidized_ldl: { label: 'Oxidized LDL', units: 'U/L', category: 'cardiometabolic', optimalRange: '< 60' },
  lp_pla2: { label: 'Lp-PLA2', units: 'nmol/min/mL', category: 'cardiometabolic', optimalRange: '< 125' },
  nt_probnp: { label: 'NT-proBNP', units: 'pg/mL', category: 'cardiometabolic', optimalRange: '< 125' },
  troponin_i: { label: 'Troponin I', units: 'ng/L', category: 'cardiometabolic', optimalRange: '< 15' },
  troponin_t: { label: 'Troponin T', units: 'ng/L', category: 'cardiometabolic', optimalRange: '< 14' },
  glucose: { label: 'Glucose', units: 'mg/dL', category: 'metabolic', optimalRange: '70-99' },
  fasting_glucose: { label: 'Fasting Glucose', units: 'mg/dL', category: 'metabolic', optimalRange: '70-99' },
  insulin: { label: 'Insulin', units: 'uIU/mL', category: 'metabolic', optimalRange: '2-8' },
  fasting_insulin: { label: 'Fasting Insulin', units: 'uIU/mL', category: 'metabolic', optimalRange: '2-8' },
  c_peptide: { label: 'C-Peptide', units: 'ng/mL', category: 'metabolic', optimalRange: '0.8-3.1' },
  hba1c: { label: 'HbA1c', units: '%', category: 'metabolic', optimalRange: '< 5.7' },
  a1c: { label: 'HbA1c', units: '%', category: 'metabolic', optimalRange: '< 5.7' },
  fructosamine: { label: 'Fructosamine', units: 'umol/L', category: 'metabolic', optimalRange: '205-285' },
  homa_ir: { label: 'HOMA-IR', units: '', category: 'metabolic', optimalRange: '< 1.5' },
  uric_acid: { label: 'Uric Acid', units: 'mg/dL', category: 'metabolic', optimalRange: '3.5-6.0' },
  lactate: { label: 'Lactate', units: 'mmol/L', category: 'metabolic', optimalRange: '0.5-2.2' },
  ketones_beta_hydroxybutyrate: { label: 'Beta-Hydroxybutyrate', units: 'mmol/L', category: 'metabolic', optimalRange: '0.2-0.8' },
  leptin: { label: 'Leptin', units: 'ng/mL', category: 'metabolic', optimalRange: '2-20' },
  adiponectin: { label: 'Adiponectin', units: 'ug/mL', category: 'metabolic', optimalRange: '> 8' },
  crp: { label: 'hs-CRP', units: 'mg/L', category: 'inflammatory', optimalRange: '< 1.0' },
  hs_crp: { label: 'hs-CRP', units: 'mg/L', category: 'inflammatory', optimalRange: '< 1.0' },
  esr: { label: 'ESR', units: 'mm/hr', category: 'inflammatory', optimalRange: '< 15' },
  homocysteine: { label: 'Homocysteine', units: 'umol/L', category: 'inflammatory', optimalRange: '< 11' },
  fibrinogen: { label: 'Fibrinogen', units: 'mg/dL', category: 'inflammatory', optimalRange: '200-400' },
  il6: { label: 'IL-6', units: 'pg/mL', category: 'inflammatory', optimalRange: '< 1.8' },
  tnf_alpha: { label: 'TNF-alpha', units: 'pg/mL', category: 'inflammatory', optimalRange: '< 8.1' },
  mcp1: { label: 'MCP-1', units: 'pg/mL', category: 'inflammatory', optimalRange: '< 200' },
  wbc: { label: 'White Blood Cells', units: 'K/uL', category: 'inflammatory', optimalRange: '4.0-10.0' },
  neutrophils: { label: 'Neutrophils', units: '%', category: 'inflammatory', optimalRange: '40-70' },
  lymphocytes: { label: 'Lymphocytes', units: '%', category: 'inflammatory', optimalRange: '20-45' },
  monocytes: { label: 'Monocytes', units: '%', category: 'inflammatory', optimalRange: '2-10' },
  eosinophils: { label: 'Eosinophils', units: '%', category: 'inflammatory', optimalRange: '0-6' },
  basophils: { label: 'Basophils', units: '%', category: 'inflammatory', optimalRange: '0-2' },
  nlr: { label: 'Neutrophil/Lymphocyte Ratio', units: '', category: 'inflammatory', optimalRange: '< 2.0' },
  tsh: { label: 'TSH', units: 'mIU/L', category: 'hormonal', optimalRange: '0.4-4.5' },
  free_t3: { label: 'Free T3', units: 'pg/mL', category: 'hormonal', optimalRange: '2.3-4.2' },
  free_t4: { label: 'Free T4', units: 'ng/dL', category: 'hormonal', optimalRange: '0.8-1.8' },
  total_t3: { label: 'Total T3', units: 'ng/dL', category: 'hormonal', optimalRange: '80-180' },
  total_t4: { label: 'Total T4', units: 'ug/dL', category: 'hormonal', optimalRange: '5.0-12.0' },
  reverse_t3: { label: 'Reverse T3', units: 'ng/dL', category: 'hormonal', optimalRange: '10-24' },
  anti_tpo: { label: 'TPO Antibodies', units: 'IU/mL', category: 'hormonal', optimalRange: '< 35' },
  anti_tg: { label: 'Thyroglobulin Antibodies', units: 'IU/mL', category: 'hormonal', optimalRange: '< 20' },
  testosterone_total: { label: 'Testosterone (Total)', units: 'ng/dL', category: 'hormonal', optimalRange: '300-900' },
  testosterone_free: { label: 'Testosterone (Free)', units: 'pg/mL', category: 'hormonal', optimalRange: '50-210' },
  shbg: { label: 'SHBG', units: 'nmol/L', category: 'hormonal', optimalRange: '20-60' },
  dhea_s: { label: 'DHEA-S', units: 'ug/dL', category: 'hormonal', optimalRange: '100-300' },
  cortisol_am: { label: 'Cortisol (AM)', units: 'ug/dL', category: 'hormonal', optimalRange: '6-18' },
  cortisol_pm: { label: 'Cortisol (PM)', units: 'ug/dL', category: 'hormonal', optimalRange: '2-9' },
  estradiol: { label: 'Estradiol', units: 'pg/mL', category: 'hormonal', optimalRange: '20-60' },
  progesterone: { label: 'Progesterone', units: 'ng/mL', category: 'hormonal', optimalRange: '0.2-20' },
  lh: { label: 'LH', units: 'mIU/mL', category: 'hormonal', optimalRange: '1.5-9.3' },
  fsh: { label: 'FSH', units: 'mIU/mL', category: 'hormonal', optimalRange: '1.4-18.1' },
  prolactin: { label: 'Prolactin', units: 'ng/mL', category: 'hormonal', optimalRange: '4-15' },
  igf1: { label: 'IGF-1', units: 'ng/mL', category: 'hormonal', optimalRange: '90-300' },
  growth_hormone: { label: 'Growth Hormone', units: 'ng/mL', category: 'hormonal', optimalRange: '0-3' },
  psa: { label: 'PSA', units: 'ng/mL', category: 'hormonal', optimalRange: '< 4.0' },
  alt: { label: 'ALT', units: 'U/L', category: 'organ_function', optimalRange: '9-46' },
  ast: { label: 'AST', units: 'U/L', category: 'organ_function', optimalRange: '10-40' },
  alp: { label: 'ALP', units: 'U/L', category: 'organ_function', optimalRange: '44-147' },
  ggt: { label: 'GGT', units: 'U/L', category: 'organ_function', optimalRange: '9-48' },
  bilirubin_total: { label: 'Total Bilirubin', units: 'mg/dL', category: 'organ_function', optimalRange: '0.2-1.2' },
  bilirubin_direct: { label: 'Direct Bilirubin', units: 'mg/dL', category: 'organ_function', optimalRange: '0.0-0.3' },
  albumin: { label: 'Albumin', units: 'g/dL', category: 'organ_function', optimalRange: '3.5-5.2' },
  globulin: { label: 'Globulin', units: 'g/dL', category: 'organ_function', optimalRange: '2.0-3.5' },
  a_g_ratio: { label: 'Albumin/Globulin Ratio', units: '', category: 'organ_function', optimalRange: '1.0-2.2' },
  total_protein: { label: 'Total Protein', units: 'g/dL', category: 'organ_function', optimalRange: '6.0-8.3' },
  creatinine: { label: 'Creatinine', units: 'mg/dL', category: 'organ_function', optimalRange: '0.7-1.3' },
  egfr: { label: 'eGFR', units: 'mL/min/1.73m2', category: 'organ_function', optimalRange: '> 90' },
  bun: { label: 'BUN', units: 'mg/dL', category: 'organ_function', optimalRange: '7-20' },
  bun_creatinine_ratio: { label: 'BUN/Creatinine Ratio', units: '', category: 'organ_function', optimalRange: '10-20' },
  cystatin_c: { label: 'Cystatin C', units: 'mg/L', category: 'organ_function', optimalRange: '0.6-1.0' },
  microalbumin: { label: 'Urine Microalbumin', units: 'mg/L', category: 'organ_function', optimalRange: '< 30' },
  uacr: { label: 'Urine Albumin/Creatinine Ratio', units: 'mg/g', category: 'organ_function', optimalRange: '< 30' },
  sodium: { label: 'Sodium', units: 'mmol/L', category: 'organ_function', optimalRange: '135-145' },
  potassium: { label: 'Potassium', units: 'mmol/L', category: 'organ_function', optimalRange: '3.5-5.1' },
  chloride: { label: 'Chloride', units: 'mmol/L', category: 'organ_function', optimalRange: '98-107' },
  bicarbonate: { label: 'Bicarbonate', units: 'mmol/L', category: 'organ_function', optimalRange: '22-29' },
  co2: { label: 'CO2', units: 'mmol/L', category: 'organ_function', optimalRange: '22-29' },
  calcium: { label: 'Calcium', units: 'mg/dL', category: 'organ_function', optimalRange: '8.6-10.2' },
  phosphorus: { label: 'Phosphorus', units: 'mg/dL', category: 'organ_function', optimalRange: '2.5-4.5' },
  urinalysis_ph: { label: 'Urinalysis pH', units: '', category: 'organ_function', optimalRange: '5.0-8.0' },
  urinalysis_specific_gravity: { label: 'Urinalysis Specific Gravity', units: '', category: 'organ_function', optimalRange: '1.005-1.030' },
  hemoglobin: { label: 'Hemoglobin', units: 'g/dL', category: 'organ_function', optimalRange: '12-17.5' },
  hematocrit: { label: 'Hematocrit', units: '%', category: 'organ_function', optimalRange: '36-50' },
  rbc: { label: 'Red Blood Cells', units: 'M/uL', category: 'organ_function', optimalRange: '4.2-5.9' },
  mcv: { label: 'MCV', units: 'fL', category: 'organ_function', optimalRange: '80-100' },
  mch: { label: 'MCH', units: 'pg', category: 'organ_function', optimalRange: '27-33' },
  mchc: { label: 'MCHC', units: 'g/dL', category: 'organ_function', optimalRange: '32-36' },
  rdw: { label: 'RDW', units: '%', category: 'organ_function', optimalRange: '11.5-14.5' },
  platelets: { label: 'Platelets', units: 'K/uL', category: 'organ_function', optimalRange: '150-400' },
  mpv: { label: 'MPV', units: 'fL', category: 'organ_function', optimalRange: '7.5-11.5' },
  vitamin_d_25_oh: { label: 'Vitamin D (25-OH)', units: 'ng/mL', category: 'micronutrient', optimalRange: '30-60' },
  vitamin_b12: { label: 'Vitamin B12', units: 'pg/mL', category: 'micronutrient', optimalRange: '400-900' },
  folate: { label: 'Folate', units: 'ng/mL', category: 'micronutrient', optimalRange: '> 7' },
  rbc_folate: { label: 'RBC Folate', units: 'ng/mL', category: 'micronutrient', optimalRange: '> 280' },
  ferritin: { label: 'Ferritin', units: 'ng/mL', category: 'micronutrient', optimalRange: '30-200' },
  iron: { label: 'Iron', units: 'ug/dL', category: 'micronutrient', optimalRange: '60-170' },
  tibc: { label: 'TIBC', units: 'ug/dL', category: 'micronutrient', optimalRange: '250-450' },
  transferrin: { label: 'Transferrin', units: 'mg/dL', category: 'micronutrient', optimalRange: '200-360' },
  transferrin_saturation: { label: 'Transferrin Saturation', units: '%', category: 'micronutrient', optimalRange: '20-45' },
  magnesium: { label: 'Magnesium', units: 'mg/dL', category: 'micronutrient', optimalRange: '1.8-2.4' },
  magnesium_rbc: { label: 'RBC Magnesium', units: 'mg/dL', category: 'micronutrient', optimalRange: '4.2-6.8' },
  zinc: { label: 'Zinc', units: 'ug/dL', category: 'micronutrient', optimalRange: '70-120' },
  copper: { label: 'Copper', units: 'ug/dL', category: 'micronutrient', optimalRange: '70-140' },
  selenium: { label: 'Selenium', units: 'ug/L', category: 'micronutrient', optimalRange: '90-140' },
  omega_3_index: { label: 'Omega-3 Index', units: '%', category: 'micronutrient', optimalRange: '> 8' },
  omega6_omega3_ratio: { label: 'Omega-6/Omega-3 Ratio', units: '', category: 'micronutrient', optimalRange: '< 5.0' },
  vitamin_b6: { label: 'Vitamin B6', units: 'ug/L', category: 'micronutrient', optimalRange: '5-50' },
  vitamin_b1: { label: 'Vitamin B1', units: 'nmol/L', category: 'micronutrient', optimalRange: '70-180' },
  vitamin_a: { label: 'Vitamin A', units: 'ug/dL', category: 'micronutrient', optimalRange: '20-80' },
  retinol: { label: 'Retinol', units: 'ug/dL', category: 'micronutrient', optimalRange: '20-80' },
  vitamin_e: { label: 'Vitamin E', units: 'mg/L', category: 'micronutrient', optimalRange: '5-20' },
  alpha_tocopherol: { label: 'Alpha-Tocopherol', units: 'mg/L', category: 'micronutrient', optimalRange: '5-20' },
  weight_kg: { label: 'Weight', units: 'kg', category: 'other', optimalRange: '' },
  body_fat: { label: 'Body Fat', units: '%', category: 'other', optimalRange: '10-25' },
  muscle_mass: { label: 'Muscle Mass', units: 'kg', category: 'other', optimalRange: '' },
  resting_heart_rate: { label: 'Resting Heart Rate', units: 'bpm', category: 'other', optimalRange: '40-60' },
  hrv: { label: 'HRV', units: 'ms', category: 'other', optimalRange: '> 50' },
  spo2: { label: 'SpO2', units: '%', category: 'other', optimalRange: '> 95' },
  respiratory_rate: { label: 'Respiratory Rate', units: 'br/min', category: 'other', optimalRange: '12-20' },
  body_temperature: { label: 'Body Temperature', units: '°C', category: 'other', optimalRange: '36.5-37.5' },
  body_temperature_delta: { label: 'Body Temp Delta', units: '°C', category: 'other', optimalRange: '-0.3 to +0.3' },
  sleep_duration: { label: 'Sleep Duration', units: 'hr', category: 'other', optimalRange: '7-9' },
  sleep_score: { label: 'Sleep Score', units: '/100', category: 'other', optimalRange: '> 80' },
  sleep_efficiency: { label: 'Sleep Efficiency', units: '%', category: 'other', optimalRange: '> 85' },
  sleep_latency: { label: 'Sleep Latency', units: 'min', category: 'other', optimalRange: '10-20' },
  sleep_rem_minutes: { label: 'Sleep REM', units: 'min', category: 'other', optimalRange: '90-140' },
  sleep_deep_minutes: { label: 'Sleep Deep', units: 'min', category: 'other', optimalRange: '60-120' },
  sleep_light_minutes: { label: 'Sleep Light', units: 'min', category: 'other', optimalRange: '180-300' },
  sleep_awake_minutes: { label: 'Sleep Awake', units: 'min', category: 'other', optimalRange: '< 45' },
  sleep_hr_avg: { label: 'Sleep HR Avg', units: 'bpm', category: 'other', optimalRange: '40-60' },
  sleep_hr_min: { label: 'Sleep HR Min', units: 'bpm', category: 'other', optimalRange: '35-55' },
  sleep_hr_max: { label: 'Sleep HR Max', units: 'bpm', category: 'other', optimalRange: '< 75' },
  snoring: { label: 'Snoring', units: 'score', category: 'other', optimalRange: 'low' },
  steps: { label: 'Steps', units: 'steps', category: 'other', optimalRange: '7000-12000' },
  distance: { label: 'Distance', units: 'km', category: 'other', optimalRange: '' },
  active_minutes: { label: 'Active Minutes', units: 'min', category: 'other', optimalRange: '30-90' },
  active_calories: { label: 'Active Calories', units: 'kcal', category: 'other', optimalRange: '' },
  total_calories: { label: 'Total Calories', units: 'kcal', category: 'other', optimalRange: '' },
  recovery_score: { label: 'Recovery Score', units: '/100', category: 'other', optimalRange: '> 70' },
  readiness: { label: 'Readiness', units: '/100', category: 'other', optimalRange: '> 70' },
  strain: { label: 'Strain', units: 'score', category: 'other', optimalRange: '10-18' },
  heart_rate_max: { label: 'Heart Rate Max', units: 'bpm', category: 'other', optimalRange: '' },
  heart_rate_min: { label: 'Heart Rate Min', units: 'bpm', category: 'other', optimalRange: '' },
  glucose_sd: { label: 'Glucose SD', units: 'mg/dL', category: 'other', optimalRange: '< 20' },
  glucose_cv: { label: 'Glucose CV', units: '%', category: 'other', optimalRange: '< 20' },
  pushups: { label: 'Push-ups', units: 'reps', category: 'other', optimalRange: '' },
  pullups: { label: 'Pull-ups', units: 'reps', category: 'other', optimalRange: '' },
  plank_seconds: { label: 'Plank Hold', units: 'sec', category: 'other', optimalRange: '' },
  bench_press: { label: 'Bench Press', units: 'kg', category: 'other', optimalRange: '' },
  '5k_time': { label: '5K Time', units: 'min', category: 'other', optimalRange: '' }
};

export const evaluateMarkerValue = (
  val: any,
  key?: string,
  sexOrContext?: string | number | null | MarkerEvaluationContext,
  context?: MarkerEvaluationContext
) => {
  if (!key) {
    return {
      status: 'unknown' as MarkerStatus,
      band: 'reference_not_available',
      direction: 'unknown' as const,
      score: 50,
      reference: { lower: null, upper: null, unit: null, source: 'unavailable', method: null },
      optimal: { lower: null, upper: null, unit: null, source: 'unavailable', method: null },
      confidence: {
        intervalSource: 'unavailable',
        unitConversionConfidence: 0,
        methodMatch: 'not_provided',
        methodMatchConfidence: 0,
        contextCoverage: 0,
        overall: 0,
        notes: ['Marker key missing.']
      }
    };
  }
  return evaluateMarkerReference({
    value: val === null || val === undefined || val === '' ? null : (typeof val === 'number' ? val : Number(val)),
    markerKey: key,
    markerConfig: biomarkersConfig[key.toLowerCase()],
    sexOrContext,
    context
  });
};
export const authedFetchJSON = apiService.authedFetchJSON;

export const fallbackProtocolBlueprints: ProtocolBlueprint[] = [];
// Helper to resolve protocol status
export const resolveProtocolStatus = (protocol: any): 'active' | 'paused' | 'completed' => {
  const raw = (protocol?.status || '').toLowerCase().trim();
  if (raw === 'paused' || raw === 'completed' || raw === 'active') return raw;
  if (protocol?.completed) return 'completed';
  if (protocol?.paused) return 'paused';
  return 'active';
};

export const moods = [
  'energetic', 'tired', 'stressed', 'calm', 'focused', 'anxious'];

export const sendAgentLog = async (data: any) => {
  // console.log('Agent Log:', data);
};

// Minimal placeholder metadata map required by recovered App build.
export const PROFILE_FIELD_META: Record<string, { label: string; type?: string; placeholder?: string }> = {};
